import { Button, makeStyles, Tooltip } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import React from 'react'

export interface HideAmountButtonProps {
  /** Whether the toggled content is currently visible (shows the "hide" affordance when true). */
  visible: boolean
  /** Label + icon shown while content is visible (click hides). Defaults to "Hide amount". */
  visibleLabel?: string
  /** Label shown while content is hidden (click shows). Defaults to "Show amount". */
  hiddenLabel?: string
  disabled?: boolean
  disabledTooltip?: React.ReactNode
  /** Render for a dark header (navy Item band): white text/icon, no border/fill (plain text button). */
  onDark?: boolean
  onToggle: (event: React.MouseEvent<HTMLElement>) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      // Borderless plain-text toggle (matches the mock): just the eye/eye-off icon + label in a muted
      // slate, no border/outline or fill, with only a subtle background wash on hover to indicate
      // click-ability. Sits in the tier-1 white section strip (Options / Payments / Products / …).
      // `&&` beats the shared Button variant rules; outline is killed in every state so there's no
      // focus/hover outline flicker.
      button: {
        textTransform: 'none',
        borderRadius: theme.borderRadius.md,
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 500,
        lineHeight: 1.2,
        minHeight: 30,
        padding: theme.spacing(0.5, 1),
        whiteSpace: 'nowrap',
        '&&, &&:hover, &&:focus, &&:active': {
          border: 'none',
          outline: 'none',
          boxShadow: 'none'
        },
        // `color` is set at `&&` (.button.button) rather than plain `.button` because `variant="text"`
        // also pulls in the shared Button's `text` class, which sets its own colour at single-class
        // specificity. That sheet mixes static and props-function rules, so react-jss attaches it
        // lazily and a same-specificity tie is decided by mount-time insertion order (see the note on
        // `dialogButtonRoot` in @paintscout/ui Button.tsx, #21962 / #22043). `&&` takes this rule out
        // of that race instead of hoping to win it. Kept off `:hover`/`:focus` so it can't outrank
        // `$onDark`'s colour below, which is a plain `&&` too and wins on source order within this sheet.
        '&&': {
          color: neutral.P60
        },
        '&:hover': {
          backgroundColor: neutral.P97
        },
        '& .MuiButton-startIcon': {
          marginRight: theme.spacing(0.75)
        },
        '&& .MuiButton-startIcon > *:first-child': {
          fontSize: 16,
          color: neutral.P50
        },
        '@media print': {
          display: 'none'
        }
      },
      // Plain white text/icon on a dark header — no border/outline or fill, subtle hover wash. Same
      // `&&` treatment as the base class above: declared after it, so at equal specificity these win.
      onDark: {
        '&&': {
          color: neutral.White
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.12)'
        },
        '&& .MuiButton-startIcon > *:first-child': {
          color: neutral.White
        }
      }
    }
  },
  { name: 'HideAmountButton' }
)

/**
 * A borderless hide/show text button with an eye / eye-off icon, used in outlined card headers
 * (estimateNext) — e.g. "Hide amount" on the Item card and "Hide" on the Payments card. No border or
 * outline in any state — only a subtle background wash on hover signals click-ability. Toggling flips
 * whether the associated content is visible; when disabled it explains why in a tooltip (mirroring the
 * old `AmountHeadColumn` behavior).
 */
export default function HideAmountButton({
  visible,
  visibleLabel = 'Hide amount',
  hiddenLabel = 'Show amount',
  disabled,
  disabledTooltip,
  onDark,
  onToggle
}: HideAmountButtonProps) {
  const classes = useStyles()

  const button = (
    <Button
      className={onDark ? `${classes.button} ${classes.onDark}` : classes.button}
      variant="text"
      size="small"
      disabled={disabled}
      startIcon={visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
      onClick={onToggle}
    >
      {visible ? visibleLabel : hiddenLabel}
    </Button>
  )

  if (disabled && disabledTooltip) {
    return (
      <Tooltip content={disabledTooltip} placement="bottom" hideIcon>
        {button}
      </Tooltip>
    )
  }

  return button
}
